import React, { useState } from "react";
import { Row, Col, Card, CardBody } from "reactstrap";
import { Helmet } from "react-helmet";

import AudioIcon from "../../assets/images/app/allaya/audio-icon.svg";
import SessionIcon from "../../assets/images/app/allaya/session-icon.svg";

import { CustomText } from "../../components/CustomText";
import SubscriptionStatus from "../../constants/SubscriptionStatus";
import MetricCard from "./MetricCard";
import GoalChip from "./GoalChip";
import { getKey, MODULE_KEYS } from "../../api/api.keys";
import { useRequestProcessor } from "../../hooks/useRequestProcessors";
import { useParams } from "react-router-dom";
import { GetUsersById, UpdateUserStatusById } from "../../api/api.service";
import { hasData } from "../../utils/commonUtils";
import { LoaderFallbackUI } from "../../components/LoaderFallbackUI";
import { monthYear } from "../../utils/dateUtils";
import { dateTimeFormat } from "../../utils/dateFormatUtils";
import CustomSwitchInput from "../../components/Input/CustomSwitchInput";
import { findStatusChecked, getStatus } from "./utils";
import { showToast, SUCCESS } from "../../utils/toastUtils";
import { ConfirmationModal } from "../../components/CustomModal";

import updateUserModalImg from "../../assets/images/app/allaya/update-icon.svg";
import { UserStatus } from "../../constants";

const recordUpdateSuccessMessage = "Status has been updated successfully";

export const MODAL_CONTENT_CONFIG = {
    [UserStatus.INACTIVE]: {
        modalTitle: "Update User",
        modalSubText: "Are you sure you want to Inactivate this user?",
        yesButtonText: "Yes",
        noButtonText: "Cancel",
        imageSrc: updateUserModalImg
    },
    [UserStatus.ACTIVE || UserStatus.UNVERIFIED]: {
        modalTitle: "Update User",
        modalSubText: "Are you sure you want to activate this user?",
        yesButtonText: "Yes",
        noButtonText: "Cancel",
        imageSrc: updateUserModalImg
    }
};

const Details = () => {
    let { userId } = useParams();

    let { useFetch, useMutate } = useRequestProcessor();

    const { data, isLoading, isFetching, refetch } = useFetch(
        getKey([MODULE_KEYS.USER_MANAGEMENT_DETAILS], { userId }),
        async ({ signal }) =>
            await GetUsersById({
                Id: userId
            })
    );
    const [modalConfig, setModalConfig] = useState(null);

    const [isUserStatusUpdating, setIsUserStatusUpdating] = useState(false);
    let { mutate } = useMutate("", getKey(), (Id, bodyData) => UpdateUserStatusById(Id, bodyData));
    const metrics1 = {
        title: "Total Minutes Listened",
        value: data?.audioMinutesListened,
        bgColor: "#e6effe"
    };
    const metrics2 = {
        title: "Sessions Completed",
        value: data?.completedSessionCount,
        bgColor: "#e6effe"
    };

    if (isFetching || isLoading || !hasData(data)) {
        return (
            <div style={{ height: "70vh" }}>
                <LoaderFallbackUI isLoader={isFetching} className={"h-100"} />
            </div>
        );
    }

    const handleChangeStatus = () => {
        let statusToSet = getStatus(data?.status);
        setModalConfig(MODAL_CONTENT_CONFIG[statusToSet]);
    };

    const handleUserStatusUpdate = () => {
        let statusToSet = getStatus(data?.status);

        const bodyData = { status: statusToSet };
        mutate(
            { Id: data?.id, bodyData },
            {
                onSuccess: async (res) => {
                    showToast(SUCCESS, recordUpdateSuccessMessage);
                    setIsUserStatusUpdating(false);
                    handleCloseModal();
                    refetch();
                },
                onError: (err) => {
                    setIsUserStatusUpdating(false);
                    handleCloseModal();
                }
            }
        );
    };

    const handleCloseModal = () => setModalConfig(null);

    const minutes = Math.floor((metrics1.value ?? 0) / 60);
    const seconds = (metrics1.value ?? 0) % 60;

    const formattedTime =
        minutes === 0 && seconds === 0
            ? "0"
            : minutes > 0
            ? seconds > 0
                ? `${minutes}m ${seconds}s`
                : `${minutes}m`
            : `${seconds}s`;

    return (
        <div className="page-content">
            <Helmet>
                <title>User Management Details</title>
            </Helmet>
            <Card className="" style={{ borderRadius: "20px" }}>
                <CardBody>
                    {/* User Info Section */}

                    <Row className="mb-4">
                        <Col md="4">
                            <CustomText
                                title={"User Name"}
                                description={data?.fullName || "-"}
                                titleClass="label-classes"
                                descClass="desc-classes"
                            />
                        </Col>
                        <Col md="4">
                            <CustomText
                                title={"Subscription Date"}
                                description={
                                    monthYear(
                                        dateTimeFormat.appDateFormat,
                                        data?.subscription?.updatedAt
                                    ) || "-"
                                }
                                titleClass="label-classes"
                                descClass="desc-classes"
                            />
                        </Col>
                        <Col md="4">
                            <CustomText
                                titleClass="label-classes"
                                descClass="desc-classes"
                                title={"Status"}
                                description={
                                    <div className="mb-0">
                                        <CustomSwitchInput
                                            label="Inactive"
                                            isChecked={findStatusChecked(
                                                data?.status,
                                                data?.fullName
                                            )}
                                            size="small"
                                            onClick={handleChangeStatus}
                                        />
                                    </div>
                                }
                            />
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col md="4">
                            <CustomText
                                title={"DOB"}
                                description={
                                    monthYear(dateTimeFormat.appDateFormat, data?.dob) || 0
                                }
                                titleClass="label-classes"
                                descClass="desc-classes"
                            />
                        </Col>

                        <Col md="4">
                            <CustomText
                                titleClass="label-classes"
                                descClass="desc-classes"
                                title={"Email"}
                                description={data.email || "-"}
                            />
                        </Col>
                        <Col md="4">
                            <CustomText
                                titleClass="label-classes"
                                descClass={`mt-1 status-common-class ${SubscriptionStatus.getLabelClass(
                                    data.tierType
                                )}`}
                                title={"Subscription Plan"}
                                description={data.tierType || "-"}
                            />
                        </Col>
                    </Row>

                    {/* Metrics Section */}
                    <Row>
                        <Col md="6">
                            <MetricCard
                                img={AudioIcon}
                                title={metrics1.title}
                                value={formattedTime}
                                bgColor={metrics1.bgColor}
                                className={"audio-card"}
                            />
                        </Col>

                        <Col md="6">
                            <MetricCard
                                img={SessionIcon}
                                title={metrics2.title}
                                value={metrics2.value}
                                bgColor={metrics2.bgColor}
                                className={"sessions-card"}
                            />
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Goals Section */}

            {data?.userGoals.length > 0 && (
                <Card className="" style={{ borderRadius: "20px" }}>
                    <CardBody>
                        <Row>
                            <Col md="12" className="mb-4">
                                <h6 className="goal-title">Main Goals</h6>
                                <div className="d-flex flex-wrap gap-2">
                                    {data?.userGoals?.map((goal, index) => {
                                        return (
                                            goal?.goal?.type === "PRIMARY" && (
                                                <GoalChip key={index} label={goal?.goal?.name} />
                                            )
                                        );
                                    })}
                                </div>
                            </Col>
                            <Col md="12" className="mt-2">
                                <h6 className="goal-title">Secondary Goals</h6>
                                <div className="d-flex flex-wrap gap-2">
                                    {data?.userGoals?.map((goal, index) => {
                                        return (
                                            goal?.goal?.type === "SECONDARY" && (
                                                <GoalChip key={index} label={goal?.goal?.name} />
                                            )
                                        );
                                    })}
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            )}

            <ConfirmationModal
                size="md"
                hasCustomConfig
                isOpen={modalConfig}
                customImageSrc={modalConfig?.imageSrc}
                customTitle={modalConfig?.modalTitle}
                customSubText={modalConfig?.modalSubText}
                customYesBtnText={modalConfig?.yesButtonText}
                customNoBtnText={modalConfig?.noButtonText}
                onYes={handleUserStatusUpdate}
                onNo={handleCloseModal}
                yesLoading={isUserStatusUpdating}
            />
        </div>
    );
};

export default Details;
